import { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';
import {
  getChartColors,
  chartDefaults,
  formatCurrency,
  chartContainerStyle,
} from '../utils/chartConfig';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

// Equipment type icons for labels
const equipmentIcons = {
  Chiller: '❄️',
  RTU: '🏢',
  CRAC: '🖥️',
  Split: '💨',
};

/**
 * EquipmentComparisonChart - Payment Option Comparison by Equipment Type
 *
 * Compares Upfront vs 3-Year payment Year 1 cash position for each equipment type.
 * Shows the cash flow advantage of 3-Year payment.
 *
 * @param {Object} byType - Equipment breakdown by type from ROI calculations
 */
export default function EquipmentComparisonChart({ byType }) {
  const theme = useTheme();
  const colors = getChartColors(theme.palette.mode);

  // Transform data for chart - compare payment options
  const chartData = useMemo(() => {
    if (!byType) return null;

    // Filter to types with equipment
    const activeTypes = Object.entries(byType).filter(
      ([_, data]) => data.count > 0
    );

    if (activeTypes.length === 0) return null;

    const labels = activeTypes.map(
      ([type]) => `${equipmentIcons[type] || ''} ${type}`
    );

    // Calculate Year 1 cash position for each payment option
    const upfrontYear1 = activeTypes.map(([_, data]) => {
      // Upfront: Pay full cost, receive savings
      return data.total_annual_savings - data.total_treatment_cost;
    });

    const threeYearYear1 = activeTypes.map(([_, data]) => {
      // 3-Year: Pay 1/3 of cost, receive savings
      const year1Payment = data.total_treatment_cost / 3;
      return data.total_annual_savings - year1Payment;
    });

    return {
      labels,
      datasets: [
        {
          label: 'Upfront (Year 1 Cash)',
          data: upfrontYear1,
          backgroundColor: colors.primary,
          borderColor: colors.primary,
          borderWidth: 0,
          borderRadius: 4,
          barPercentage: 0.8,
          categoryPercentage: 0.7,
        },
        {
          label: '3-Year (Year 1 Cash)',
          data: threeYearYear1,
          backgroundColor: colors.teal,
          borderColor: colors.teal,
          borderWidth: 0,
          borderRadius: 4,
          barPercentage: 0.8,
          categoryPercentage: 0.7,
        },
      ],
      // Store raw data for tooltips
      _rawData: activeTypes,
    };
  }, [byType]);

  // Chart options
  const options = useMemo(() => {
    return {
      ...chartDefaults,
      plugins: {
        ...chartDefaults.plugins,
        legend: {
          ...chartDefaults.plugins.legend,
          display: true,
          labels: {
            color: colors.grey,
            usePointStyle: true,
            pointStyle: 'rect',
            padding: 20,
          },
        },
        datalabels: {
          display: true,
          color: (context) => {
            // White text for positive values, keep readable for negative
            return context.dataset.data[context.dataIndex] >= 0
              ? colors.white
              : '#0A0A0A';
          },
          anchor: (context) => {
            return context.dataset.data[context.dataIndex] >= 0 ? 'center' : 'end';
          },
          align: (context) => {
            return context.dataset.data[context.dataIndex] >= 0 ? 'center' : 'top';
          },
          font: {
            family: '"JetBrains Mono", monospace',
            size: 11,
            weight: 700,
          },
          formatter: (value) => {
            const sign = value >= 0 ? '+' : '';
            if (Math.abs(value) >= 1000) {
              return `${sign}$${(value / 1000).toFixed(1)}K`;
            }
            return `${sign}$${value.toFixed(0)}`;
          },
        },
        tooltip: {
          ...chartDefaults.plugins.tooltip,
          callbacks: {
            title: (items) => {
              // Remove emoji from title
              return items[0].label.replace(/[^\w\s]/g, '').trim();
            },
            label: (context) => {
              const label = context.dataset.label;
              const value = context.parsed.y;
              const sign = value >= 0 ? '+' : '';
              return `${label}: ${sign}${formatCurrency(value)}`;
            },
            afterBody: (items) => {
              // Add extra context from raw data
              if (!chartData?._rawData) return [];

              const index = items[0].dataIndex;
              const [_, data] = chartData._rawData[index];

              const upfrontNet = data.total_annual_savings - data.total_treatment_cost;
              const threeYearNet = data.total_annual_savings - (data.total_treatment_cost / 3);
              const advantage = threeYearNet - upfrontNet;

              return [
                '',
                `Units: ${data.count}`,
                `Annual Savings: ${formatCurrency(data.total_annual_savings)}`,
                `Treatment Cost: ${formatCurrency(data.total_treatment_cost)}`,
                '',
                `3-Year Year 1 Advantage: +${formatCurrency(advantage)}`,
              ];
            },
          },
        },
      },
      scales: {
        x: {
          ...chartDefaults.scales.x,
          ticks: {
            ...chartDefaults.scales.x.ticks,
            font: {
              size: 12,
              weight: 600,
            },
          },
        },
        y: {
          ...chartDefaults.scales.y,
          type: 'linear',
          position: 'left',
          title: {
            display: true,
            text: 'Year 1 Cash Position ($)',
            color: colors.grey,
            font: {
              size: 11,
            },
          },
          ticks: {
            ...chartDefaults.scales.y.ticks,
            callback: (value) => {
              const sign = value >= 0 ? '+' : '';
              return `${sign}${formatCurrency(value)}`;
            },
          },
          grid: {
            ...chartDefaults.scales.y.grid,
            // Add zero line
            color: (context) => {
              if (context.tick.value === 0) {
                return 'rgba(255, 255, 255, 0.3)';
              }
              return 'rgba(255, 255, 255, 0.05)';
            },
            lineWidth: (context) => {
              if (context.tick.value === 0) {
                return 2;
              }
              return 1;
            },
          },
        },
      },
    };
  }, [chartData]);

  if (!chartData) {
    return null;
  }

  return (
    <Box sx={{ my: 4 }}>
      {/* Section Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            letterSpacing: '-0.01em',
            mb: 0.5,
          }}
        >
          Payment Option Comparison by Equipment
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Year 1 cash position: Upfront vs 3-Year payment per equipment type
        </Typography>
      </Box>

      {/* Chart Container */}
      <Box sx={{ ...chartContainerStyle, height: 350 }}>
        <Bar data={chartData} options={options} />
      </Box>

      {/* Insight */}
      <Box
        sx={{
          mt: 2,
          p: 1.5,
          borderRadius: 1,
          background: 'rgba(78, 205, 196, 0.08)',
          borderLeft: `3px solid ${colors.teal}`,
        }}
      >
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem' }}>
          <strong>Key Insight:</strong> 3-Year payment keeps Year 1 cash positive across all equipment types.
          Upfront payment shows negative Year 1 cash when treatment cost exceeds annual savings.
        </Typography>
      </Box>
    </Box>
  );
}
